import Header from '~/components/route/Header';
import { Button } from '~/components/ui/button';
import appleSignUpButton from '~/assets/apple_signup_button.png';
import { Form, Link, useActionData, useRouteError, useSubmit } from '@remix-run/react';
import { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction, redirect } from '@remix-run/node';
import { commitSession, getSession } from '~/sessions';
import { AUTH_ACTION, SIGNUP, TOKEN } from '~/constants/cookies';
import { useForm, SubmissionResult } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import TermsAndPrivacyPolicy from '~/components/auth/TermsAndPrivacyPolicy';
import MethodDivider from '~/components/auth/MethodDivider';
import NavBar from '~/components/navBar/NavBar';
import useIsLoading from '~/hooks/useIsLoading';
import { getLoginSchema } from '~/schemas/getLoginSchema';
import { PageRoutes } from '~/constants/routes';
import AuthEmailField from '~/components/auth/AuthEmailField';
import AuthPasswordField from '~/components/auth/AuthPasswordField';
import ErrorMessage from '~/components/formFields/ErrorMessage';
import { handleAppleOAuthRequest } from '~/utils/appleOAuth';
import { handleGoogleOAuthRequest } from '~/utils/googleOAuth';
import { handleForgotPassword } from '~/utils/handleForgotPassword';
import { handleAuthWithPassword } from '~/utils/handleAuthWithPassword';
import { handleCheckEmail } from '~/utils/handleCheckEmail';
import GoogleSignUpIcon from '~/components/icons/GoogleSignUpIcon';
import React from 'react';
import { fireSignUpActioned, fireViewedSignup } from '~/analytics/auth';
import useAnonymousId from '~/analytics/useAnonymousId';

export const meta: MetaFunction = () => {
  return [{ title: 'Create an account | Custody Navigator' }];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  const token = session.get(TOKEN);
  if (token) {
    return redirect(PageRoutes.TEMPLATES, {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    });
  }

  return null;
}

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();
  const intent = formData.get('intent');
  const url = new URL(request.url);
  session.set(AUTH_ACTION, SIGNUP);

  if (intent === 'apple') return handleAppleOAuthRequest(session, url);
  if (intent === 'google') return handleGoogleOAuthRequest(session, url);

  /** Clicked forgot password, sending OTP */
  const email = formData.get('email') as string;
  if (intent === 'forgotPassword') return handleForgotPassword(session, email);

  /** Auth via email and password */
  const password = formData.get('password') as string;
  if (email && password) return handleAuthWithPassword({ email, password, session });

  /* Initial email check: if no password on file, fire OTP; otherwise, show password field */
  return handleCheckEmail(email, session);
}

export default function SignUp() {
  const submit = useSubmit();
  const isLoading = useIsLoading();
  const anonymousId = useAnonymousId();
  const lastResult = useActionData<SubmissionResult<typeof action> & { authError?: string; pwdOnFile?: boolean }>();
  const isShowPassword = Boolean(lastResult?.pwdOnFile);
  const [form, fields] = useForm({
    lastResult,

    onValidate({ formData, submitter }) {
      const isOAuthClick = submitter?.id === 'googleButton' || submitter?.id === 'appleButton';
      const intent = formData.get('intent');
      const isEmailRequired = isOAuthClick ? false : intent === 'email';
      const isPasswordRequired = isOAuthClick ? false : isShowPassword && intent !== 'forgotPassword';
      return parseWithZod(formData, { schema: getLoginSchema({ isEmailRequired, isPasswordRequired }) });
    },

    onSubmit(event, { formData }) {
      const intent = formData.get('intent');
      if (intent === 'google') return fireSignUpActioned({ action: 'submitted', anonymousId, selection: 'google' });
      if (intent === 'apple') return fireSignUpActioned({ action: 'submitted', anonymousId, selection: 'apple' });

      const email = formData.get('email');
      const password = formData.get('password');
      if (email && !password) fireSignUpActioned({ action: 'submitted', anonymousId, selection: 'email' });
    },

    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
  });

  const handleLogout = () => {
    submit({ intent: 'logout' }, { method: 'POST' });
  };

  React.useEffect(() => {
    if (anonymousId) fireViewedSignup(anonymousId);
  }, [anonymousId]);

  return (
    <div className="flex min-h-screen flex-col bg-neutral-offWhite">
      <Form method="POST" id={form.id} onSubmit={form.onSubmit} noValidate>
        <button className="hidden" type="submit" name="intent" value="email" />
        <NavBar
          isAuthenticated={false}
          onLogout={handleLogout}
          userData={{ email: '', name: '', scheduleExists: false }}
        />
        <div className="mb-24 px-4 sm:px-0">
          <div className="mx-auto my-0 flex max-w-96 flex-col bg-neutral-offWhite py-4 screen-md:max-w-[852px]">
            <Header
              title="Create your free account"
              description="Create your free account to create your schedule, save changes, add holidays, and more."
            />

            <div className="flex items-center justify-center py-8">
              <Button
                className="flex h-16 w-[360px] items-center justify-center bg-[#131314]"
                disabled={isLoading}
                id="googleButton"
                name="intent"
                type="submit"
                value="google"
              >
                <GoogleSignUpIcon />
              </Button>
            </div>

            <div className="flex items-center justify-center pb-8">
              <Button
                className="flex h-16 w-[360px] items-center justify-center p-0"
                disabled={isLoading}
                id="appleButton"
                name="intent"
                type="submit"
                value="apple"
              >
                <picture className="flex h-full w-full">
                  <img width={360} height={64} src={appleSignUpButton} alt="" loading="eager" />
                </picture>
              </Button>
            </div>

            <MethodDivider />

            <div className="flex items-center justify-center py-6">
              <div className="relative flex w-full flex-col items-center justify-center">
                <div className="flex w-full max-w-[360px] flex-col items-center px-1">
                  <AuthEmailField id={fields?.email.id} name={fields?.email.name} errors={fields?.email?.errors} />
                  {isShowPassword ? (
                    <AuthPasswordField
                      id={fields?.password.id}
                      name={fields?.password.name}
                      errors={fields?.password?.errors}
                    />
                  ) : null}
                </div>
                <Button
                  className="flex h-14 w-full max-w-[360px] items-center justify-center bg-green-dark text-base font-semibold text-neutral-offWhite"
                  type="submit"
                  name="intent"
                  value="email"
                  disabled={isLoading}
                >
                  Continue
                </Button>
                {lastResult?.authError ? <ErrorMessage error={lastResult.authError} /> : null}
              </div>
            </div>

            <TermsAndPrivacyPolicy />

            <div className="flex items-center justify-center py-8">
              {isShowPassword ? (
                <button name="intent" value="forgotPassword" className="text-sm font-medium text-green-dark underline">
                  Forgot your password?
                </button>
              ) : (
                <p className="text-center text-sm font-light text-brown-dark">
                  Already have an account?{' '}
                  <Link to="/login" className="font-bold text-green-dark">
                    Log in.
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  console.error('<ErrorBoundary /> signup', error);

  return (
    <h1 className="flex h-screen items-center justify-center bg-neutral-offWhite font-sans text-lg font-light">
      Something went wrong - please refresh the page or{' '}
      <Link to={PageRoutes.LOGIN} className="flex items-center justify-center pl-1 uppercase underline">
        click here
      </Link>
    </h1>
  );
}
